import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';

export type VisibleFieldKey =
  | 'title'
  | 'subtitle'
  | 'description'
  | 'shopContactMail'
  | 'infoTitle'
  | 'infoDescription'
  | 'redirectUrl'
  | 'urlName'
  | 'infoImageUrl'
  | 'infoVideoUrl'
  | 'itemQuantity';

export interface ComponentInfoFormValue {
  infoId: number;
  infoTitle: string;
  infoDescription: string;
  redirectUrl: string;
  urlName: string;
  imageURL: string;
  blob: { file: File; key: string } | null;
  videoURL: string;
  videoBlob: { file: File; key: string } | null;
  videoManualURL: string;
  videoUrlType: number;
  previousVideoUrl: string;
  previousManualUrl: string;
  isYouTube: boolean;
}

export interface SerializedComponentPayload {
  descriptions: string;
  redirectUrls: string;
  infoTitles: string;
  urlNames: string;
  imageUrls: string;
  videoUrls: string;
  videoUrlTypes: string;
}

export interface ComponentTranslationInfoValue {
  infoTitle: string;
  infoDescription: string;
  redirectUrl: string;
  urlName: string;
}

export interface ComponentTranslationValue {
  manual: boolean;
  title: string;
  subtitle: string;
  description: string;
  infoFields: ComponentTranslationInfoValue[];
}

export interface ResolvedEnglishTranslation {
  value: ComponentTranslationValue;
  hasExistingTranslation: boolean;
  isManual: boolean;
}

export const COMPONENT_FIELD_VISIBILITY_CONFIG: Record<number, VisibleFieldKey[]> = {
  1: ['infoImageUrl', 'infoTitle', 'infoDescription', 'redirectUrl', 'urlName'],
  2: ['infoImageUrl', 'infoDescription', 'redirectUrl'],
  3: ['title', 'infoImageUrl', 'infoTitle', 'infoDescription', 'redirectUrl', 'urlName'],
  4: ['title', 'description', 'infoImageUrl', 'infoTitle', 'infoDescription', 'redirectUrl'],
  5: ['title', 'description', 'infoImageUrl', 'infoTitle', 'infoDescription', 'redirectUrl'],
  6: ['title', 'description', 'infoTitle', 'infoDescription', 'redirectUrl'],
  7: ['infoImageUrl', 'title', 'description', 'infoTitle', 'infoDescription', 'redirectUrl', 'urlName'],
  8: ['title', 'subtitle', 'itemQuantity'],
  9: ['title', 'subtitle', 'itemQuantity'],
  10: ['title', 'subtitle', 'itemQuantity'],
  11: ['infoImageUrl', 'title', 'description', 'infoTitle', 'infoDescription', 'redirectUrl', 'urlName'],
  12: ['infoImageUrl', 'redirectUrl'],
  13: ['title', 'subtitle', 'description', 'redirectUrl'],
  14: ['title', 'subtitle', 'description', 'infoTitle', 'infoDescription', 'redirectUrl', 'urlName', 'infoImageUrl'],
  15: ['infoImageUrl', 'redirectUrl', 'urlName'],
  16: ['infoImageUrl', 'redirectUrl'],
  17: ['infoVideoUrl', 'redirectUrl', 'infoTitle', 'infoDescription'],
  18: ['infoVideoUrl', 'infoTitle', 'infoDescription', 'redirectUrl', 'urlName'],
  19: ['title', 'infoImageUrl', 'redirectUrl'],
  20: ['title', 'subtitle', 'infoImageUrl', 'redirectUrl'],
  21: ['title', 'subtitle', 'infoImageUrl', 'redirectUrl'],
  22: ['infoVideoUrl', 'redirectUrl'],
  23: ['title', 'infoImageUrl', 'infoTitle', 'infoDescription', 'redirectUrl', 'urlName'],
  24: ['title', 'description', 'shopContactMail'],
  25: ['title', 'description', 'infoImageUrl', 'infoTitle', 'infoDescription', 'redirectUrl'],
  26: ['title', 'description', 'infoImageUrl', 'infoTitle', 'infoDescription', 'redirectUrl'],
  27: ['title', 'description', 'infoImageUrl', 'infoTitle', 'infoDescription', 'redirectUrl'],
};

const COMPONENT_DYNAMIC_FIELDS: VisibleFieldKey[] = [
  'title',
  'subtitle',
  'description',
  'shopContactMail',
  'itemQuantity',
];

export function buildVisibleFields(componentTypeId: number): Set<VisibleFieldKey> {
  return new Set(COMPONENT_FIELD_VISIBILITY_CONFIG[componentTypeId] || []);
}

export function createComponentForm(fb: FormBuilder): FormGroup {
  return fb.group({
    component: fb.group({
      componentId: [0],
      componentName: ['', Validators.required],
      title: [''],
      subtitle: [''],
      description: [''],
      shopContactMail: [''],
      componentTypeId: [0, Validators.required],
      status: [0],
      position: [0],
      itemQuantity: [0],
      itemListId: [0],
      itemSort: [0],
      categoryId: [0],
      subCategoryId: [0],
      subSubCategoryId: [0],
      collectionId: [0],
      pageId: [0],
    }),
    infoFields: fb.array([]),
  });
}

export function createInfoFieldGroup(
  fb: FormBuilder,
  visibleFields: Set<VisibleFieldKey>,
  seed: Partial<ComponentInfoFormValue> = {}
): FormGroup {
  const group = fb.group({
    infoId: [seed.infoId ?? 0],
    infoTitle: [seed.infoTitle ?? ''],
    infoDescription: [seed.infoDescription ?? ''],
    redirectUrl: [seed.redirectUrl ?? '', [optionalHttpUrlValidator()]],
    urlName: [seed.urlName ?? ''],
    imageURL: [seed.imageURL ?? ''],
    blob: [seed.blob ?? null],
    videoURL: [seed.videoURL ?? ''],
    videoBlob: [seed.videoBlob ?? null],
    videoManualURL: [seed.videoManualURL ?? ''],
    videoUrlType: [Number(seed.videoUrlType ?? (visibleFields.has('infoVideoUrl') ? 1 : 0))],
    previousVideoUrl: [seed.previousVideoUrl ?? ''],
    previousManualUrl: [seed.previousManualUrl ?? ''],
    isYouTube: [seed.isYouTube ?? false],
  });

  applyInfoFieldValidators(group, visibleFields);
  return group;
}

export function applyDynamicValidators(
  componentGroup: FormGroup,
  infoFields: FormArray,
  visibleFields: Set<VisibleFieldKey>
): void {
  COMPONENT_DYNAMIC_FIELDS.forEach((field) => {
    const control = componentGroup.get(field);
    if (!control) {
      return;
    }

    control.clearValidators();

    if (visibleFields.has(field)) {
      if (field === 'shopContactMail') {
        control.setValidators([Validators.required, Validators.email]);
      } else if (field === 'itemQuantity') {
        control.setValidators([Validators.required, Validators.min(1)]);
      } else {
        control.setValidators([richTextRequiredValidator()]);
      }
    }

    control.updateValueAndValidity({ emitEvent: false });
  });

  infoFields.controls.forEach((control) => {
    applyInfoFieldValidators(control as FormGroup, visibleFields);
  });
}

export function applyInfoFieldValidators(group: FormGroup, visibleFields: Set<VisibleFieldKey>): void {
  setControlValidators(group, 'infoTitle', visibleFields.has('infoTitle') ? [richTextRequiredValidator()] : []);
  setControlValidators(group, 'infoDescription', visibleFields.has('infoDescription') ? [richTextRequiredValidator()] : []);
  setControlValidators(group, 'urlName', visibleFields.has('urlName') ? [Validators.required] : []);
  setControlValidators(group, 'imageURL', visibleFields.has('infoImageUrl') ? [Validators.required] : []);
  setControlValidators(group, 'redirectUrl', [optionalHttpUrlValidator()]);
}

export function serializeInfoFields(infoFields: ComponentInfoFormValue[]): SerializedComponentPayload {
  return {
    descriptions: infoFields.map((field) => sanitizeString(field.infoDescription)).join('|'),
    redirectUrls: infoFields.map((field) => sanitizeString(field.redirectUrl)).join('|'),
    infoTitles: infoFields.map((field) => sanitizeString(field.infoTitle)).join('|'),
    urlNames: infoFields.map((field) => sanitizeString(field.urlName)).join('|'),
    imageUrls: infoFields.map((field) => field.imageURL || '').join('|'),
    videoUrls: infoFields
      .map((field) => (Number(field.videoUrlType) === 2 ? field.videoManualURL || field.videoURL || '' : field.videoURL || ''))
      .join('|'),
    videoUrlTypes: infoFields.map((field) => String(field.videoUrlType ?? 0)).join('|'),
  };
}

export function createEmptyTranslatedInfoField(): ComponentTranslationInfoValue {
  return {
    infoTitle: '',
    infoDescription: '',
    redirectUrl: '',
    urlName: '',
  };
}

export function createEmptyTranslatedState(infoFieldCount: number = 0): ComponentTranslationValue {
  return {
    manual: false,
    title: '',
    subtitle: '',
    description: '',
    infoFields: Array.from({ length: infoFieldCount }, () => createEmptyTranslatedInfoField()),
  };
}

export function cloneTranslatedState(value: ComponentTranslationValue): ComponentTranslationValue {
  return {
    manual: value.manual === true,
    title: sanitizeString(value.title),
    subtitle: sanitizeString(value.subtitle),
    description: sanitizeString(value.description),
    infoFields: value.infoFields.map((field) => ({
      infoTitle: sanitizeString(field.infoTitle),
      infoDescription: sanitizeString(field.infoDescription),
      redirectUrl: sanitizeString(field.redirectUrl),
      urlName: sanitizeString(field.urlName),
    })),
  };
}

export function buildLocalTranslatedState(component: any, infoFields: ComponentInfoFormValue[]): ComponentTranslationValue {
  return {
    manual: false,
    title: sanitizeString(component?.title),
    subtitle: sanitizeString(component?.subtitle),
    description: sanitizeString(component?.description),
    infoFields: infoFields.map((field) => ({
      infoTitle: sanitizeString(field.infoTitle),
      infoDescription: sanitizeString(field.infoDescription),
      redirectUrl: sanitizeString(field.redirectUrl),
      urlName: sanitizeString(field.urlName),
    })),
  };
}

export function ensureTranslatedInfoFieldCount(
  value: ComponentTranslationValue,
  fallbackInfoFields: ComponentTranslationInfoValue[]
): ComponentTranslationValue {
  return {
    manual: value.manual === true,
    title: sanitizeString(value.title),
    subtitle: sanitizeString(value.subtitle),
    description: sanitizeString(value.description),
    infoFields: fallbackInfoFields.map((fallbackField, index) => ({
      infoTitle: sanitizeString(value.infoFields[index]?.infoTitle ?? fallbackField.infoTitle),
      infoDescription: sanitizeString(value.infoFields[index]?.infoDescription ?? fallbackField.infoDescription),
      redirectUrl: sanitizeString(value.infoFields[index]?.redirectUrl ?? fallbackField.redirectUrl),
      urlName: sanitizeString(value.infoFields[index]?.urlName ?? fallbackField.urlName),
    })),
  };
}

export function buildEnglishTranslationState(
  rawTranslatedContent: unknown,
  component: any,
  infoFields: ComponentInfoFormValue[]
): ResolvedEnglishTranslation {
  const fallback = buildLocalTranslatedState(component, infoFields);
  const parsed = parseTranslatedContent(rawTranslatedContent);
  const rawEnglish = parsed?.en;

  if (!rawEnglish || typeof rawEnglish !== 'object') {
    return {
      value: fallback,
      hasExistingTranslation: false,
      isManual: false,
    };
  }

  const rawEnglishInfoFields = Array.isArray(rawEnglish.infoFields) ? rawEnglish.infoFields : [];
  const translatedInfoById = new Map<number, any>();

  rawEnglishInfoFields.forEach((field: any) => {
    const translatedInfoId = Number(field?.infoId || 0);
    if (translatedInfoId > 0 && !translatedInfoById.has(translatedInfoId)) {
      translatedInfoById.set(translatedInfoId, field);
    }
  });

  const normalizedInfoFields = fallback.infoFields.map((fallbackField, index) => {
    const currentInfoId = Number(infoFields[index]?.infoId || 0);
    const matchedById = currentInfoId > 0 ? translatedInfoById.get(currentInfoId) : null;
    const matchedByIndex = rawEnglishInfoFields[index];
    const matchedField = matchedById || matchedByIndex || null;

    return {
      infoTitle: sanitizeString(matchedField?.infoTitle ?? fallbackField.infoTitle),
      infoDescription: sanitizeString(matchedField?.infoDescription ?? fallbackField.infoDescription),
      redirectUrl: sanitizeString(matchedField?.redirectUrl ?? fallbackField.redirectUrl),
      urlName: sanitizeString(matchedField?.urlName ?? fallbackField.urlName),
    };
  });

  const normalized = ensureTranslatedInfoFieldCount(
    {
      manual: rawEnglish.manual === true,
      title: sanitizeString(rawEnglish.title ?? fallback.title),
      subtitle: sanitizeString(rawEnglish.subtitle ?? fallback.subtitle),
      description: sanitizeString(rawEnglish.description ?? fallback.description),
      infoFields: normalizedInfoFields,
    },
    fallback.infoFields
  );

  return {
    value: normalized,
    hasExistingTranslation: hasTranslatedValueContent(normalized),
    isManual: normalized.manual === true,
  };
}

export function buildManualTranslatedPayload(value: ComponentTranslationValue): { en: ComponentTranslationValue } {
  const normalized = cloneTranslatedState(value);
  normalized.manual = true;
  return {
    en: normalized,
  };
}

export function shouldSendManualTranslatedContent(
  currentValue: ComponentTranslationValue,
  initialValue: ComponentTranslationValue,
  manualEnabled: boolean,
  persistedManual: boolean
): boolean {
  if (!manualEnabled) {
    return false;
  }

  if (persistedManual) {
    return true;
  }

  return !areTranslatedStatesEqual(currentValue, initialValue);
}

export function hasTranslatedValueContent(value: ComponentTranslationValue): boolean {
  if (hasMeaningfulText(value.title) || hasMeaningfulText(value.subtitle) || hasMeaningfulText(value.description)) {
    return true;
  }

  return value.infoFields.some((field) => {
    return (
      hasMeaningfulText(field.infoTitle) ||
      hasMeaningfulText(field.infoDescription) ||
      sanitizeString(field.redirectUrl).length > 0 ||
      sanitizeString(field.urlName).length > 0
    );
  });
}

export function sanitizeString(str: any): string {
  if (!str) {
    return '';
  }

  return String(str).trim();
}

export function isOptionalHttpUrlValid(value: string | null | undefined): boolean {
  const normalizedValue = sanitizeString(value);
  if (!normalizedValue) {
    return true;
  }

  return /^https?:\/\/.+/i.test(normalizedValue);
}

export function optionalHttpUrlValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    return isOptionalHttpUrlValid(control.value) ? null : { invalidUrl: true };
  };
}

export function richTextRequiredValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    return hasMeaningfulText(control.value) ? null : { required: true };
  };
}

export function hasMeaningfulText(value: string | null | undefined): boolean {
  if (value == null) {
    return false;
  }

  const normalized = String(value)
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  return normalized.length > 0;
}

export function isYouTubeUrl(url: string): boolean {
  return /youtube\.com|youtu\.be/i.test(url || '');
}

export function mapApiInfoToFormSeed(info: any): Partial<ComponentInfoFormValue> {
  const rawVideoUrl = info?.infoVideoUrl || '';
  const numericVideoType = Number(info?.videoUrlType || (rawVideoUrl ? 1 : 0));
  const manualUrl = numericVideoType === 2 ? info?.videoManualURL || rawVideoUrl || '' : '';
  const uploadedVideoUrl = numericVideoType === 1 ? rawVideoUrl : '';

  return {
    infoId: Number(info?.infoId || 0),
    infoTitle: info?.infoTitle || '',
    infoDescription: info?.infoDescription || '',
    redirectUrl: info?.redirectUrl || '',
    urlName: info?.urlName || '',
    imageURL: info?.infoImageUrl || '',
    blob: null,
    videoURL: uploadedVideoUrl,
    videoBlob: null,
    videoManualURL: manualUrl,
    videoUrlType: numericVideoType,
    previousVideoUrl: uploadedVideoUrl,
    previousManualUrl: manualUrl,
    isYouTube: isYouTubeUrl(manualUrl || rawVideoUrl),
  };
}

function parseTranslatedContent(rawTranslatedContent: unknown): any | null {
  if (!rawTranslatedContent) {
    return null;
  }

  if (typeof rawTranslatedContent === 'string') {
    try {
      return JSON.parse(rawTranslatedContent);
    } catch (error) {
      console.error('Invalid translatedContent JSON payload:', error);
      return null;
    }
  }

  return typeof rawTranslatedContent === 'object' ? rawTranslatedContent : null;
}

function areTranslatedStatesEqual(left: ComponentTranslationValue, right: ComponentTranslationValue): boolean {
  const normalizedLeft = cloneTranslatedState(left);
  const normalizedRight = cloneTranslatedState(right);

  normalizedLeft.manual = false;
  normalizedRight.manual = false;

  return JSON.stringify(normalizedLeft) === JSON.stringify(normalizedRight);
}

function setControlValidators(controlGroup: FormGroup, controlName: string, validators: ValidatorFn[]): void {
  const control = controlGroup.get(controlName);
  if (!control) {
    return;
  }

  control.clearValidators();
  control.setValidators(validators);
  control.updateValueAndValidity({ emitEvent: false });
}
